import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { CardComp, ButtonComp } from "../../../components";
import PageWrapper from "../modules/PageWrapper";
import axios from "axios";
import {
  extractAlatpayTransactionId,
  paymentConfirmationUrl,
} from "../../../utils/paymentConfirmationRoutes";

const API_BASE = process.env.REACT_APP_BASE_URL;

export default function AlatpayCheckout() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState("loading");
  const [message, setMessage] = useState("Preparing ALATPay checkout…");
  const [checkout, setCheckout] = useState(null);

  const urlReference =
    searchParams.get("reference") || searchParams.get("trxref");
  const storedReference = sessionStorage.getItem("pending_payment_reference");
  const paymentReference = urlReference || storedReference;

  const confirmationHref = paymentConfirmationUrl(paymentReference);

  useEffect(() => {
    if (!paymentReference) {
      setStatus("error");
      setMessage("Payment reference is missing.");
    }
  }, [paymentReference]);

  useEffect(() => {
    if (!paymentReference) return undefined;
    let cancelled = false;

    const load = async () => {
      try {
        const res = await axios.get(
          `${API_BASE}/public/payments/alatpay-checkout?reference=${encodeURIComponent(
            paymentReference
          )}`
        );
        const data = res?.data?.data;
        if (cancelled) return;
        if (!data?.checkout) {
          setStatus("error");
          setMessage(res?.data?.message || "Could not load ALATPay checkout.");
          return;
        }
        setCheckout(data.checkout);
        setStatus("ready");
      } catch (err) {
        if (cancelled) return;
        setStatus("error");
        setMessage(
          err?.response?.data?.message ||
            err?.message ||
            "Could not load ALATPay checkout."
        );
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [paymentReference]);

  const scriptUrl = useMemo(
    () => checkout?.scriptUrl || "https://web.alatpay.ng/js/alatpay.js",
    [checkout]
  );

  useEffect(() => {
    if (status !== "ready" || !checkout) return undefined;

    const existing = document.querySelector("script[data-alatpay]");
    const goToConfirmation = (txnId) => {
      const href = paymentConfirmationUrl(paymentReference, null, {
        alatpay_transaction_id: txnId || undefined,
      });
      window.location.href = href;
    };

    const attachAndConfirm = async (response) => {
      const txnId = extractAlatpayTransactionId(response);
      setStatus("confirming");
      setMessage(
        txnId
          ? "Payment received. Confirming with ALATPay…"
          : "Payment received. Opening confirmation…"
      );

      if (txnId && paymentReference) {
        sessionStorage.setItem("alatpay_transaction_id", txnId);
        try {
          // Wait for attach (+ optional verify) BEFORE leaving checkout.
          // ALATPay Get-Single needs this UUID; our orderId alone is not enough.
          await axios.post(
            `${API_BASE}/public/payments/alatpay-attach-transaction`,
            {
              reference: paymentReference,
              transaction_id: String(txnId),
              verify: true,
            },
            { timeout: 45000 }
          );
        } catch (_err) {
          // Still continue — confirmation page will retry with the ID in the URL.
        }
      }

      goToConfirmation(txnId);
    };

    const start = () => {
      if (!window.Alatpay?.setup) {
        setStatus("error");
        setMessage("ALATPay script did not load. Please refresh and try again.");
        return;
      }
      const popup = window.Alatpay.setup({
        apiKey: checkout.apiKey,
        businessId: checkout.businessId,
        email: checkout.email,
        phone: checkout.phone,
        firstName: checkout.firstName,
        lastName: checkout.lastName,
        metadata: checkout.metadata,
        currency: checkout.currency || "NGN",
        amount: checkout.amount,
        onTransaction: function (response) {
          attachAndConfirm(response);
        },
        onClose: function () {
          navigate(confirmationHref);
        },
      });
      popup.show();
    };

    if (existing && window.Alatpay) {
      start();
      return undefined;
    }

    const script = document.createElement("script");
    script.src = scriptUrl;
    script.async = true;
    script.dataset.alatpay = "true";
    script.onload = start;
    script.onerror = () => {
      setStatus("error");
      setMessage("Could not load the ALATPay payment script.");
    };
    document.body.appendChild(script);

    return undefined;
  }, [status, checkout, scriptUrl, paymentReference, navigate, confirmationHref]);

  return (
    <PageWrapper title="ALATPay checkout">
      <div className="px-3 px-lg-4 py-5 d-flex justify-content-center">
        <CardComp cardClassName="border-0" style={{ maxWidth: 520, width: "100%" }}>
          <div className="p-4 text-center">
            <h1 className="h4 fw-semibold mb-3">Pay with ALATPay</h1>
            <p className="text-muted">{message}</p>
            {status === "error" && (
              <ButtonComp
                text="Go to confirmation"
                onClick={() => navigate(confirmationHref)}
              />
            )}
          </div>
        </CardComp>
      </div>
    </PageWrapper>
  );
}
